// Guest order token on the frontend (QA r3 privacy low).
// New emailed links carry the token in the URL fragment: /orders/<id>#gt=<token>[&confirmPickup=1].
// A fragment is never sent to a server, so the token no longer lands in access logs. Links already
// emailed (/orders/<id>?guestToken=<token>) keep working. Either way the order page:
//   1. reads the token (fragment, then legacy query, then this tab's sessionStorage),
//   2. keeps it in sessionStorage for this order (reloads keep working),
//   3. removes it from the address bar (history replace), keeping other params,
//   4. sends it to the API in the X-Guest-Token header (GET) or the JSON body (PATCH), never in a
//      GET query string.

export const GUEST_TOKEN_HEADER = 'X-Guest-Token';
const LEGACY_QUERY_KEYS = ['guestToken', 'token'];
const storageKey = (orderId) => `cherrypkd.guestOrderToken.${orderId}`;

const clean = (v) => (typeof v === 'string' ? v.trim() : '');
const hashParams = (hash) => new URLSearchParams(String(hash || '').replace(/^#/, ''));

// Token present in the URL itself (fragment first, then the legacy query param).
export function guestTokenFromUrl(search, hash) {
  const fromHash = clean(hashParams(hash).get('gt'));
  if (fromHash) return fromHash;
  const q = new URLSearchParams(search || '');
  for (const k of LEGACY_QUERY_KEYS) {
    const v = clean(q.get(k));
    if (v) return v;
  }
  return '';
}

// true when kept (the caller only removes the token from the URL if it could be kept).
export function rememberGuestOrderToken(orderId, token) {
  if (!orderId || !token) return false;
  try {
    window.sessionStorage.setItem(storageKey(orderId), token);
    return window.sessionStorage.getItem(storageKey(orderId)) === token;
  } catch { return false; } // storage off: leave the link as it is
}

export function storedGuestOrderToken(orderId) {
  if (!orderId) return '';
  try { return clean(window.sessionStorage.getItem(storageKey(orderId))); } catch { return ''; }
}

// The token for this order page: URL first, then the copy kept for this tab.
export function readGuestOrderToken(orderId, search, hash) {
  return guestTokenFromUrl(search, hash) || storedGuestOrderToken(orderId);
}

// &confirmPickup=1 may sit in the fragment (new links) or the query (old links / after cleanup).
export function confirmPickupRequested(search, hash) {
  return hashParams(hash).get('confirmPickup') === '1'
    || new URLSearchParams(search || '').get('confirmPickup') === '1';
}

// Same path without the token: legacy query keys and the #gt fragment are dropped; every other query
// param stays, and a fragment confirmPickup=1 moves to the query so the page still highlights it.
export function urlWithoutGuestToken(pathname, search, hash) {
  const q = new URLSearchParams(search || '');
  LEGACY_QUERY_KEYS.forEach((k) => q.delete(k));
  const h = hashParams(hash);
  if (h.get('confirmPickup') === '1') q.set('confirmPickup', '1');
  h.delete('gt');
  h.delete('confirmPickup');
  const qs = q.toString();
  const hs = h.toString();
  return `${pathname}${qs ? `?${qs}` : ''}${hs ? `#${hs}` : ''}`;
}

// Axios config for a guest GET: the token rides in a header, not in the URL.
export function guestTokenConfig(token) {
  return token ? { headers: { [GUEST_TOKEN_HEADER]: token } } : undefined;
}

// /orders/<id> (an order, not ?type=booking) opened with a guest token in the URL or kept for
// this tab: the route gates let it through without an account (the API checks the token).
export function isGuestOrderPath(location) {
  const p = String(location?.pathname || '').replace(/\/+$/, '');
  const m = /^\/orders\/([^/]+)$/.exec(p);
  if (!m) return false;
  if ((new URLSearchParams(location.search || '').get('type') || 'order') !== 'order') return false;
  return !!readGuestOrderToken(decodeURIComponent(m[1]), location.search, location.hash);
}

// In-app link to a guest order (checkout confirmation): token in the fragment.
export function guestOrderPath(orderId, token) {
  return token ? `/orders/${orderId}#gt=${encodeURIComponent(token)}` : `/orders/${orderId}`;
}
